/**
 * Centralized date utility functions
 * All dates in the system should be formatted as MM/dd/yyyy for display.
 * Use formatDateForApi() when sending dates in API payloads (YYYY-MM-DD with hyphens).
 */

/**
 * Converts a date to YYYY-MM-DD format (hyphens) for API payloads.
 * Accepts MM/dd/yyyy, YYYY-MM-DD, ISO strings, or Date. Returns empty string if null/invalid.
 */
export function formatDateForApi(dateInput: string | Date | number | null | undefined): string {
    if (dateInput === null || dateInput === undefined) return '';
    if (dateInput === '') return '';

    try {
        let date: Date;

        if (dateInput instanceof Date) {
            date = dateInput;
        } else if (typeof dateInput === 'number') {
            date = new Date(dateInput);
        } else {
            const dateString = String(dateInput).trim();
            if (!dateString) return '';

            // Already YYYY-MM-DD
            if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
                return dateString.split('T')[0].substring(0, 10);
            }

            // MM/dd/yyyy or MM-dd-yyyy
            if (dateString.match(/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}$/)) {
                const parts = dateString.split(/[\/\-]/);
                if (parts.length === 3) {
                    const month = parseInt(parts[0], 10);
                    const day = parseInt(parts[1], 10);
                    const year = parseInt(parts[2], 10);
                    if (parts[0].length === 1 || parts[1].length === 1) {
                        date = new Date(year, month - 1, day);
                    } else {
                        date = new Date(year, month - 1, day);
                    }
                    if (!isNaN(date.getTime())) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, '0');
                        const d = String(date.getDate()).padStart(2, '0');
                        return `${y}-${m}-${d}`;
                    }
                }
            }

            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
}

/**
 * Formats a date string, Date object, or timestamp to MM/dd/yyyy format.
 * Handles: ISO strings, YYYY-MM-DD, MM/dd/yyyy, DD-MM-YYYY, numeric timestamp.
 * @param dateInput - Date string, Date object, or number (ms since epoch)
 * @returns Formatted date string in MM/dd/yyyy format, or empty string if null/invalid
 */
export function formatDate(dateInput: string | Date | number | null | undefined): string {
    if (dateInput === null || dateInput === undefined) return '';
    if (dateInput === '') return '';

    try {
        let date: Date;

        if (dateInput instanceof Date) {
            date = dateInput;
        } else if (typeof dateInput === 'number') {
            date = new Date(dateInput);
        } else {
            const dateString = String(dateInput).trim();
            if (!dateString) return '';

            // Already MM/dd/yyyy
            if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                return dateString;
            }

            // YYYY-MM-DD at start (optional time / Z). Calendar date in local zone — avoids prior-day display
            // when the API sends midnight UTC (e.g. 2026-04-24T00:00:00.000Z) for a business "date paid".
            const ymd = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (ymd) {
                const year = parseInt(ymd[1], 10);
                const month = parseInt(ymd[2], 10) - 1;
                const day = parseInt(ymd[3], 10);
                date = new Date(year, month, day);
                if (isNaN(date.getTime())) {
                    date = new Date(dateString);
                }
            } else if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
                // DD-MM-YYYY or D-M-YYYY
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    date = new Date(year, month, day);
                } else {
                    date = new Date(dateString);
                }
            } else {
                date = new Date(dateString);
            }
        }

        if (isNaN(date.getTime())) return '';

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    } catch {
        return '';
    }
}

/**
 * Formats a date string or Date object to MM/dd/yyyy HH:mm AM/PM format
 * @param dateInput - Date string or Date object
 * @returns Formatted date-time string, or empty string if invalid
 */
export function formatDateTime(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    
    try {
        let date: Date;
        
        if (dateInput instanceof Date) {
            date = dateInput;
        } else {
            date = new Date(dateInput);
        }
        
        if (isNaN(date.getTime())) {
            return '';
        }
        
        // Format date as MM/dd/yyyy
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        const dateStr = `${month}/${day}/${year}`;
        
        // Format time as HH:mm AM/PM
        const hours24 = date.getHours();
        const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const timeStr = `${hours12}:${minutes} ${ampm}`;
        
        return `${dateStr} ${timeStr}`;
    } catch {
        return '';
    }
}

/**
 * Formats a date string or Date object to MM/dd/yyyy HH:mm:ss AM/PM format
 * @param dateInput - Date string or Date object
 * @returns Formatted date-time string with seconds, or empty string if invalid
 */
export function formatDateTimeWithSeconds(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';
    
    try {
        let date: Date;
        
        if (dateInput instanceof Date) {
            date = dateInput;
        } else {
            date = new Date(dateInput);
        }
        
        if (isNaN(date.getTime())) {
            return '';
        }
        
        // Format date as MM/dd/yyyy
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        const dateStr = `${month}/${day}/${year}`;
        
        // Format time as HH:mm:ss AM/PM
        const hours24 = date.getHours();
        const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const timeStr = `${hours12}:${minutes}:${seconds} ${ampm}`;
        
        return `${dateStr} ${timeStr}`;
    } catch {
        return '';
    }
}

/**
 * Formats a date for display as M/d/yyyy (no leading zeros on month/day), e.g. 8/16/2010.
 * Handles ISO strings, YYYY-MM-DD, MM/dd/yyyy, M/d/yyyy, and numeric timestamps.
 */
export function formatDateMdyyyy(dateInput: string | Date | number | null | undefined): string {
    if (dateInput === null || dateInput === undefined || dateInput === '') return '';

    try {
        let date: Date;

        if (dateInput instanceof Date) {
            date = dateInput;
        } else if (typeof dateInput === 'number') {
            const n = dateInput;
            const asStr = String(Math.trunc(n));
            if (/^\d{8}$/.test(asStr)) {
                const year = parseInt(asStr.slice(0, 4), 10);
                const month = parseInt(asStr.slice(4, 6), 10) - 1;
                const day = parseInt(asStr.slice(6, 8), 10);
                date = new Date(year, month, day);
            } else if (n > 1e11) {
                date = new Date(n);
            } else if (n > 20000 && n < 60000) {
                // OLE/Excel serial date (days since 1899-12-30).
                date = new Date(Math.round((n - 25569) * 86400000));
            } else {
                date = new Date(n);
            }
        } else {
            const dateString = String(dateInput).trim();
            if (!dateString) return '';

            if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                return dateString;
            }

            if (/^\d{8}$/.test(dateString)) {
                const year = parseInt(dateString.slice(0, 4), 10);
                const month = parseInt(dateString.slice(4, 6), 10) - 1;
                const day = parseInt(dateString.slice(6, 8), 10);
                date = new Date(year, month, day);
            } else {
                const ymd = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (ymd) {
                    const year = parseInt(ymd[1], 10);
                    const month = parseInt(ymd[2], 10) - 1;
                    const day = parseInt(ymd[3], 10);
                    date = new Date(year, month, day);
                    if (isNaN(date.getTime())) {
                        date = new Date(dateString);
                    }
                } else if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    const parts = dateString.split('/');
                    date = new Date(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10));
                } else {
                    date = new Date(dateString);
                }
            }
        }

        if (isNaN(date.getTime())) return '';

        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch {
        return '';
    }
}

/**
 * Formats a date-time for display as M/d/yyyy h:mm:ss AM/PM (no leading zeros on month/day/hour),
 * e.g. 3/19/2026 7:01:37 AM. Parses ISO strings like 2026-03-20T09:25:23 in local time.
 */
export function formatDateTimeMdyyyyWithSeconds(dateInput: string | Date | number | null | undefined): string {
    if (dateInput === null || dateInput === undefined || dateInput === '') return '';

    try {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return '';

        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        const dateStr = `${month}/${day}/${year}`;

        const hours24 = date.getHours();
        const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const timeStr = `${hours12}:${minutes}:${seconds} ${ampm}`;

        return `${dateStr} ${timeStr}`;
    } catch {
        return '';
    }
}

/**
 * Delphi FollowUpViewDetailsFM DF_MMDDYYYY_HHNNSS — MM/dd/yyyy HH:mm:ss (local time, 24-hour).
 * Example: 06/15/2026 15:31:28
 */
export function formatFollowUpDoeDelphi(dateInput: string | Date | null | undefined): string {
    if (dateInput === null || dateInput === undefined || dateInput === '') return '';

    try {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return '';

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
        return '';
    }
}

/**
 * Formats an ISO date string (e.g. from API with Z) to MM/dd/yyyy hh:mm:ss AM/PM in UTC.
 * Use when the response time should be shown in the same timezone as the API (e.g. modifiedDate/changedDate).
 */
export function formatDateTimeWithSecondsUTC(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '';

    try {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return '';

        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const year = date.getUTCFullYear();
        const dateStr = `${month}/${day}/${year}`;

        const hours24 = date.getUTCHours();
        const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        const timeStr = `${hours12}:${minutes}:${seconds} ${ampm}`;

        return `${dateStr} ${timeStr}`;
    } catch {
        return '';
    }
}
